/**
 * Cross-workflow Approvals queue (M26 PR 3) — /operations/approvals.
 *
 * Single pane of glass over the 4 approval streams (leave / loan /
 * excuse / termination). Reuses the existing
 * /api/mission-control/pending-approvals endpoint, which already
 * unifies the streams with their summary + detail_link.
 *
 * Tabs:
 *   - All — every pending approval the user can see (bounded by
 *     legal entity).
 *   - Mine — only items where the resolved approver is the current
 *     user (sourced from the ApproverProvider, the same selector the
 *     top bar uses today).
 *
 * Row click → navigates to the workflow's detail surface. Per the
 * brief, inline approve/deny is deferred to a follow-up — when the
 * 4 workflow-specific approve endpoints accept the cross-workflow
 * detail URL pattern, those buttons land here.
 */

import type { ColumnDef } from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import {
  Banknote,
  CalendarDays,
  ClipboardCheck,
  Inbox,
  Plus,
  ShieldAlert,
  UserMinus,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  DataTable,
  EmptyState,
  PageHeader,
} from "../components/ui";
import { api } from "../lib/api";
import type { PendingApprovalItem } from "../lib/api";
import { useApproverContext } from "../lib/approver";
import { useLegalEntityGate } from "../lib/legalEntity";

type Scope = "all" | "mine";

const _TYPE_META: Record<
  PendingApprovalItem["workflow_type"],
  { labelKey: string; icon: typeof CalendarDays; pillClass: string }
> = {
  leave_request: {
    labelKey: "operations.approvals_filter_leave",
    icon: CalendarDays,
    pillClass:
      "bg-sky-50 text-sky-700 border-sky-200",
  },
  loan_application: {
    labelKey: "operations.approvals_filter_loan",
    icon: Banknote,
    pillClass:
      "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  excuse_request: {
    labelKey: "operations.approvals_filter_excuse",
    icon: ShieldAlert,
    pillClass:
      "bg-amber-50 text-amber-700 border-amber-200",
  },
  termination: {
    labelKey: "mission_control.workflow_eos",
    icon: UserMinus,
    pillClass:
      "bg-rose-50 text-rose-700 border-rose-200",
  },
};

function _daysPending(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

export function ApprovalsQueuePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { legalEntityId: le, gate } = useLegalEntityGate();

  // The top-bar approver picker is the source of truth for "who am
  // I acting as." Empty string ⇒ no approver selected (anonymous).
  const approver = useApproverContext().approverEmployeeId;

  const [scope, setScope] = useState<Scope>("all");
  const [typeFilter, setTypeFilter] = useState<
    PendingApprovalItem["workflow_type"] | ""
  >("");

  const q = useQuery({
    queryKey: ["approvals-queue", le, scope, approver],
    queryFn: () =>
      api.missionControlPendingApprovals({
        legalEntityId: le || undefined,
        approverEmployeeId:
          scope === "mine" && approver ? approver : undefined,
        limitPerType: 100,
      }),
    enabled: !!le,
  });

  const rows = useMemo(() => {
    const all = q.data ?? [];
    if (!typeFilter) return all;
    return all.filter((r) => r.workflow_type === typeFilter);
  }, [q.data, typeFilter]);

  const columns = useMemo<ColumnDef<PendingApprovalItem, unknown>[]>(
    () => [
      {
        id: "workflow_type",
        header: t("common.type", "Type"),
        accessorFn: (r) => t(_TYPE_META[r.workflow_type].labelKey),
        cell: ({ row }) => {
          const meta = _TYPE_META[row.original.workflow_type];
          const Icon = meta.icon;
          return (
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${meta.pillClass}`}
            >
              <Icon size={12} aria-hidden />
              {t(meta.labelKey)}
            </span>
          );
        },
        size: 110,
      },
      {
        id: "requester",
        header: t("operations.approvals_col_requester"),
        accessorFn: (r) =>
          r.employee_name_en ?? r.employee_id.slice(0, 8),
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-sm font-medium text-text-primary">
              {row.original.employee_name_en ?? "—"}
            </span>
            <span className="text-xs text-text-tertiary">
              {row.original.employee_code ?? "—"}
            </span>
          </div>
        ),
      },
      {
        id: "summary",
        header: t("operations.approvals_col_summary"),
        accessorKey: "summary",
        cell: ({ row }) => (
          <span className="text-sm text-text-secondary">
            {row.original.summary}
          </span>
        ),
      },
      {
        id: "submitted_at",
        header: t("operations.approvals_col_submitted"),
        accessorKey: "created_at",
        cell: ({ row }) => (
          <span className="text-sm text-text-secondary">
            {new Date(row.original.created_at).toLocaleDateString()}
          </span>
        ),
        size: 120,
      },
      {
        id: "days_pending",
        header: t("operations.approvals_pending_badge"),
        accessorFn: (r) => _daysPending(r.created_at),
        cell: ({ row }) => {
          const d = _daysPending(row.original.created_at);
          const tone =
            d >= 7
              ? "bg-rose-100 text-rose-700"
              : d >= 3
              ? "bg-amber-100 text-amber-700"
              : "bg-surface-sunken text-text-secondary";
          return (
            <span
              className={`rounded px-1.5 py-0.5 text-xs font-medium ${tone}`}
            >
              {d}
              {t("mission_control.days_short")}
            </span>
          );
        },
        size: 90,
      },
      {
        id: "current_approver",
        header: t("operations.approvals_col_approver"),
        accessorFn: (r) => r.current_approver_employee_id ?? "",
        cell: ({ row }) => {
          const id = row.original.current_approver_employee_id;
          if (!id)
            return <span className="text-xs text-text-tertiary">—</span>;
          const isMe = approver && id === approver;
          return (
            <span
              className={
                isMe
                  ? "rounded-full bg-brand text-brand-fg px-2 py-0.5 text-xs font-semibold"
                  : "font-mono text-xs text-text-tertiary"
              }
            >
              {isMe ? t("common.you", "You") : id.slice(0, 8)}
            </span>
          );
        },
        size: 100,
      },
    ],
    [approver, t],
  );

  if (gate) return gate;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("nav.approvals")}
        subtitle={t("operations.approvals_subtitle")}
        breadcrumbs={[
          { label: t("nav.operations"), href: "/operations" },
          { label: t("nav.approvals") },
        ]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/operations/transactions/leave/new")}
              className="inline-flex items-center gap-1 rounded border border-border-subtle bg-white px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-sunken"
            >
              <Plus className="h-3 w-3" />
              {t("operations.approvals.cta.leave", "New leave")}
            </button>
            <button
              type="button"
              onClick={() => navigate("/operations/transactions/loan/new")}
              className="inline-flex items-center gap-1 rounded border border-border-subtle bg-white px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-sunken"
            >
              <Plus className="h-3 w-3" />
              {t("operations.approvals.cta.loan", "New loan")}
            </button>
            <button
              type="button"
              onClick={() => navigate("/operations/transactions/excuse/new")}
              className="inline-flex items-center gap-1 rounded border border-border-subtle bg-white px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-sunken"
            >
              <Plus className="h-3 w-3" />
              {t("operations.approvals.cta.excuse", "New excuse")}
            </button>
          </div>
        }
      />

      {/* Scope tabs (All / Mine). Team is a "I'm a manager of one of
          the approvers" view — needs the reports-to walk, a
          follow-up. */}
      <div className="flex items-center gap-2 border-b border-border-subtle">
        {(["all", "mine"] as Scope[]).map((s) => {
          const active = scope === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setScope(s)}
              className={[
                "px-3 py-1.5 text-sm font-medium border-b-2 -mb-px",
                active
                  ? "border-brand text-text-primary"
                  : "border-transparent text-text-tertiary hover:text-text-primary",
              ].join(" ")}
            >
              {s === "all" ? t("common.all") : t("common.mine")}
            </button>
          );
        })}
        {scope === "mine" && !approver && (
          <p className="ms-3 text-xs text-amber-700">
            {t("common.pick_approver_hint")}
          </p>
        )}
      </div>

      {/* Workflow-type filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setTypeFilter("")}
          className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
            typeFilter === ""
              ? "border-brand bg-brand text-brand-fg"
              : "border-border-subtle bg-surface-default text-text-secondary hover:bg-surface-sunken"
          }`}
        >
          {t("common.all_types")}
        </button>
        {(Object.keys(_TYPE_META) as PendingApprovalItem["workflow_type"][]).map(
          (k) => (
            <button
              key={k}
              type="button"
              onClick={() => setTypeFilter(typeFilter === k ? "" : k)}
              className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
                typeFilter === k
                  ? _TYPE_META[k].pillClass + " border-current"
                  : "border-border-subtle bg-surface-default text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t(_TYPE_META[k].labelKey)}
            </button>
          ),
        )}
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={q.isLoading}
        exportFilename="approvals"
        onRowClick={(r) => navigate(r.detail_link)}
        emptyState={
          <EmptyState
            icon={Inbox}
            headline={t("operations.approvals_empty_headline")}
            description={t("operations.approvals_empty_body")}
          />
        }
      />

      <p className="text-xs text-text-tertiary">
        <ClipboardCheck size={12} className="me-1 inline" aria-hidden />
        {t("common.inline_approve_hint")}
      </p>
    </div>
  );
}
